import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ref as dbRef, get, remove } from 'firebase/database';
import { ref as storageRef, listAll, getDownloadURL, uploadBytes, deleteObject } from 'firebase/storage';
import { database, storage } from '../../Firebase/firebaseconfig';
import { buildBillPDF, resolveTotal } from './billPDF';
import { ArrowLeft, Download, RefreshCw, FileX, Trash2 } from 'lucide-react';
import './admin-page.css';

function fmtINR(n) {
  return '₹' + Number(n).toLocaleString('en-IN');
}

export default function BillDetails() {
  const { invoiceNumber } = useParams();
  const navigate = useNavigate();
  const [bill, setBill] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [showRegenDialog, setShowRegenDialog] = useState(false);
  const [regening, setRegening] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    get(dbRef(database, `bill/${invoiceNumber}`))
      .then(snap => {
        if (snap.exists()) setBill(snap.val());
        else setError('Bill not found.');
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [invoiceNumber]);

  const tryDownload = async () => {
    const folderRef = storageRef(storage, `bills/${invoiceNumber}`);
    const result = await listAll(folderRef);
    if (result.items.length === 0) throw new Error('NO_PDF');
    const url = await getDownloadURL(result.items[0]);
    window.open(url, '_blank');
  };

  const handleDownload = async () => {
    setDownloading(true);
    setError('');
    try {
      await tryDownload();
    } catch (err) {
      if (err.message === 'NO_PDF') {
        setShowRegenDialog(true);
      } else {
        setError('Download failed: ' + err.message);
      }
    } finally {
      setDownloading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete invoice #${invoiceNumber} for ${bill?.customerName}? This cannot be undone.`)) return;
    setDeleting(true);
    setError('');
    try {
      try {
        const result = await listAll(storageRef(storage, `bills/${invoiceNumber}`));
        await Promise.all(result.items.map(item => deleteObject(item)));
      } catch {}
      await remove(dbRef(database, `bill/${invoiceNumber}`));
      navigate('/admin/dashboard/view-bills');
    } catch (err) {
      setError('Delete failed: ' + err.message);
      setDeleting(false);
    }
  };

  const handleRegenerate = async () => {
    if (!bill) return;
    setRegening(true);
    setError('');
    try {
      const total = resolveTotal(bill);
      const pdfBlob = await buildBillPDF(
        invoiceNumber,
        bill.customerName,
        bill.phoneNumber,
        bill.dateOfPurchase,
        bill.items || [],
        total,
        bill.address || ''
      );
      const filename = `${(bill.customerName || 'bill').replace(/\s+/g, '_')}_${(bill.dateOfPurchase || '').replace(/\//g, '-')}_${Date.now()}.pdf`;
      await uploadBytes(
        storageRef(storage, `bills/${invoiceNumber}/${filename}`),
        pdfBlob,
        { contentType: 'application/pdf' }
      );
      setShowRegenDialog(false);
      try { await tryDownload(); } catch {}
    } catch (err) {
      setError('Regeneration failed: ' + err.message);
    } finally {
      setRegening(false);
    }
  };

  if (loading) return <div className="ap-loading">Loading…</div>;

  const total = bill ? resolveTotal(bill) : 0;

  return (
    <div className="ap-page">
      <div className="ap-header">
        <button className="ap-back-btn" onClick={() => navigate('/admin/dashboard/view-bills')}>
          <ArrowLeft size={18} />
        </button>
        <div>
          <h1 className="ap-title">Invoice #{invoiceNumber}</h1>
          <p className="ap-subtitle" style={{ wordBreak: 'break-word' }}>{bill?.customerName}</p>
        </div>
      </div>

      {error && <div className="ap-error">{error}</div>}

      {bill && (
        <div className="ap-card">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18, marginBottom: 22 }}>
            <div>
              <div className="bill-detail-label">Customer</div>
              <div className="bill-detail-value">{bill.customerName}</div>
            </div>
            <div>
              <div className="bill-detail-label">Phone</div>
              <div className="bill-detail-value">{bill.phoneNumber}</div>
            </div>
            <div>
              <div className="bill-detail-label">Date</div>
              <div className="bill-detail-value">{bill.dateOfPurchase}</div>
            </div>
            <div>
              <div className="bill-detail-label">Invoice #</div>
              <div className="bill-detail-value">{invoiceNumber}</div>
            </div>
            {bill.address && (
              <div style={{ gridColumn: '1 / -1' }}>
                <div className="bill-detail-label">Address</div>
                <div className="bill-detail-value">{bill.address}</div>
              </div>
            )}
          </div>

          <p className="ap-section-title">Items</p>
          <div className="bill-table-wrap">
            <table className="bill-table">
              <thead>
                <tr>
                  {['Item', 'Qty', 'Price', 'Amount', 'Warranty', 'Serial Number'].map(h => (
                    <th key={h}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(bill.items || []).map((item, i) => (
                  <tr key={i}>
                    <td>{item.itemName}</td>
                    <td>{item.quantity}</td>
                    <td>{fmtINR(item.price)}</td>
                    <td style={{ fontWeight: 500 }}>{fmtINR(Number(item.quantity) * Number(item.price))}</td>
                    <td style={{ color: (!item.warranty || item.warranty === 'No') ? '#9ca3af' : '#059669' }}>
                      {item.warranty || 'No'}
                    </td>
                    <td style={{ color: item.serialNumber ? undefined : '#9ca3af' }}>
                      {item.serialNumber || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bill-total-bar" style={{ marginTop: 14 }}>
            <span>Total Amount</span>
            <span className="bill-total-amount">{fmtINR(total)}</span>
          </div>

          <div className="ap-btn-row">
            <button className="ap-btn ap-btn-primary" onClick={handleDownload} disabled={downloading || regening} style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
              <Download size={16} />
              {downloading ? 'Opening…' : 'Download PDF'}
            </button>
            <button
              className="ap-btn ap-btn-danger"
              onClick={handleDelete}
              disabled={deleting || downloading || regening}
              style={{ display: 'flex', alignItems: 'center', gap: 6 }}
            >
              <Trash2 size={15} />
              {deleting ? 'Deleting…' : 'Delete Bill'}
            </button>
          </div>
        </div>
      )}

      {/* Regenerate dialog */}
      {showRegenDialog && (
        <div className="regen-overlay">
          <div className="regen-dialog">
            <div className="regen-dialog-icon"><FileX size={44} strokeWidth={1.2} color="#f59e0b" /></div>
            <h3>Bill PDF Not Found</h3>
            <p>The PDF for invoice #{invoiceNumber} is missing from storage. Would you like to regenerate it from the saved bill data?</p>
            <div className="regen-dialog-btns">
              <button
                className="ap-btn ap-btn-primary"
                onClick={handleRegenerate}
                disabled={regening}
                style={{ display: 'flex', alignItems: 'center', gap: 7 }}
              >
                <RefreshCw size={15} />
                {regening ? 'Regenerating…' : 'Yes, Regenerate'}
              </button>
              <button className="ap-btn ap-btn-ghost" onClick={() => setShowRegenDialog(false)} disabled={regening}>
                No, Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
